import { useState, ReactNode } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Grid from '@cloudscape-design/components/grid';
import Steps from '@cloudscape-design/components/steps';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';

export type StepStatus = 'success' | 'error' | 'loading' | 'pending';

export interface LifecycleStage {
  name: string;
  label?: string;
  status: StepStatus;
  isReached: boolean;
  timestamp?: string;
}

interface LifecycleTrackerProps {
  title: string;
  description?: string;
  stages: LifecycleStage[];
  renderDetail?: (stageName: string) => ReactNode;
}

const formatTime = (ts?: string): string => {
  if (!ts) return '';
  return new Date(ts).toLocaleString();
};

export const LifecycleTracker = ({ title, description, stages, renderDetail }: LifecycleTrackerProps) => {
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleToggle = (stageName: string) => {
    if (selectedStage === stageName && showDetails) {
      setShowDetails(false);
    } else {
      setSelectedStage(stageName);
      setShowDetails(true);
    }
  };

  const steps = stages.map((stage) => ({
    status: stage.status,
    header: (
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <Box fontWeight="bold">{stage.label || stage.name.replace(/_/g, ' ')}</Box>
        {stage.isReached && renderDetail && (
          <Button variant="inline-link" onClick={() => handleToggle(stage.name)}>
            {selectedStage === stage.name && showDetails ? 'Hide' : 'Details'}
          </Button>
        )}
      </SpaceBetween>
    ),
    details: stage.timestamp ? (
      <Box fontSize="body-s" color="text-body-secondary">{formatTime(stage.timestamp)}</Box>
    ) : undefined,
  }));

  const stageList = <Steps steps={steps} />;
  const detailContent = showDetails && selectedStage && renderDetail ? renderDetail(selectedStage) : null;

  return (
    <Container header={<Header variant="h2" description={description}>{title}</Header>}>
      {detailContent ? (
        <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
          {stageList}
          <div>{detailContent}</div>
        </Grid>
      ) : stageList}
    </Container>
  );
};
